/*
 * NOTE
 *
 * At the moment, we have the updated designs for photos
 * but not for icons, prepaid, etc.
 * In the mean time, CheckoutForm2 will pull from CheckoutForm
 * sub-components and then add only the parts that are changing.
 * Eventually, once we have the completed designs, we can merge
 * CheckoutForm & CheckoutForm2.
 */

import React, { useState } from "react";
import PropTypes from "prop-types";

// components:
import Heading from "core/Heading";
import OrderPreview from "./OrderPreview";
import OrderTotal from "./OrderTotal";
// TODO - copy these components over once we can deprecate the original CheckoutForm
import CardLinks from "core/CheckoutForm/CardLinks";
import SavedCardForm from "core/CheckoutForm/SavedCardForm";
import EmptyForm from "core/CheckoutForm/EmptyForm";
import FormButtons from "core/CheckoutForm/FormButtons";

// styles:
import colors from "core/colors";
import { StyledPhotoCheckoutForm, GeneralFormError } from "./styles";

const CheckoutForm = ({
    className,
    hideCardLinks,
    // CardLinks props:
    hasSavedCard, // also used in other components
    saveCardCheckboxOnClick,
    saveCardInitialValue,
    // SavedCardForm/EmptyForm props:
    response,
    form,
    handleFormChange,
    generalFormError,
    // OrderPreview & OrderTotal props:
    selectedPrice,
    title,
    thumbnail,
    slugWithId,
    licenseSchemas,
    // FormButtons props:
    isSubmitDisabled,
    submitFormWithNewCardInfo,
    submitFormWithSavedCardInfo,
    cancelLink
}) => {
    const [showSavedCard, setShowSavedCard] = useState(hasSavedCard);

    const handleUseCardClick = e => {
        e.preventDefault();
        setShowSavedCard(!showSavedCard);
    };

    return (
        <div className={className}>
            <OrderPreview
                licenseSchemas={licenseSchemas}
                title={title}
                thumbnail={thumbnail}
                slugWithId={slugWithId}
            />

            <Heading
                type="h2"
                size="xs"
                bottomSpacing="lg"
                headingColor={colors.midnight}
            >
                Payment Information
            </Heading>

            {!hideCardLinks && (
                <CardLinks
                    showSavedCard={showSavedCard}
                    hasSavedCard={hasSavedCard}
                    handleUseCardClick={handleUseCardClick}
                    saveCardCheckboxOnClick={saveCardCheckboxOnClick}
                    saveCardInitialValue={saveCardInitialValue}
                />
            )}

            <StyledPhotoCheckoutForm>
                <form className="stripe-form">
                    {showSavedCard ? (
                        <SavedCardForm response={response} />
                    ) : (
                        <EmptyForm
                            form={form}
                            handleFormChange={handleFormChange}
                        />
                    )}

                    {!!generalFormError && (
                        <GeneralFormError>{generalFormError}</GeneralFormError>
                    )}

                    <OrderTotal totalPrice={selectedPrice} />

                    <FormButtons
                        isSubmitDisabled={isSubmitDisabled}
                        showSavedCard={showSavedCard}
                        submitFormWithNewCardInfo={submitFormWithNewCardInfo}
                        submitFormWithSavedCardInfo={
                            submitFormWithSavedCardInfo
                        }
                        cancelLink={cancelLink}
                    />
                </form>
            </StyledPhotoCheckoutForm>
        </div>
    );
};

CheckoutForm.defaultProps = {
    hideCardLinks: false
};

CheckoutForm.propTypes = {
    className: PropTypes.string,
    hideCardLinks: PropTypes.bool,
    hasSavedCard: PropTypes.bool.isRequired,
    saveCardCheckboxOnClick: PropTypes.func.isRequired,
    saveCardInitialValue: PropTypes.bool.isRequired,
    response: PropTypes.object,
    form: PropTypes.object,
    handleFormChange: PropTypes.func.isRequired,
    generalFormError: PropTypes.string,
    selectedPrice: PropTypes.string.isRequired, // price currently selected by LicenseRadio options; can be changed
    title: PropTypes.string.isRequired,
    thumbnail: PropTypes.string.isRequired,
    slugWithId: PropTypes.string.isRequired,
    licenseSchemas: PropTypes.arrayOf(PropTypes.object).isRequired,
    isSubmitDisabled: PropTypes.bool.isRequired,
    submitFormWithNewCardInfo: PropTypes.func.isRequired,
    submitFormWithSavedCardInfo: PropTypes.func.isRequired,
    cancelLink: PropTypes.string.isRequired
};

export default CheckoutForm;
